import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthProvider';
import { getSettings } from './services/api';
import LoginPage from './pages/LoginPage';
import CreditCheckPage from './pages/CreditCheckPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import FieldMappingsPage from './pages/FieldMappingsPage';
import ProcessedClientsPage from './pages/ProcessedClientsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import PublicCreditCheckPage from './pages/PublicCreditCheckPage';
import './App.css';

function AppContent() {
  const { user, loading, logout } = useAuth();
  const [appTitle, setAppTitle] = useState('Valid8');
  const [environment, setEnvironment] = useState('staging');

  useEffect(() => {
    if (user) {
      getSettings()
        .then(data => {
          if (data.app_title) setAppTitle(data.app_title);
          if (data.active_environment) setEnvironment(data.active_environment);
        })
        .catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    document.title = appTitle;
  }, [appTitle]);

  if (loading) {
    return (
      <div className="app">
        <div className="loading-screen">
          <div className="loading-spinner" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage appTitle={appTitle} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-brand">
          <h1>{appTitle}</h1>
          <span className={`env-badge ${environment}`}>
            {environment === 'production' ? 'PROD' : 'STG'}
          </span>
        </div>
        <nav className="header-nav">
          <NavLink to="/" end>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
            Credit Check
          </NavLink>
          <NavLink to="/mappings">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            Mappings
          </NavLink>
          <NavLink to="/clients">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            Clients
          </NavLink>
          <NavLink to="/logs">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            Audit Logs
          </NavLink>
          <NavLink to="/settings">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Settings
          </NavLink>
        </nav>
        <div className="user-info">
          <div className="user-avatar">{user.email[0].toUpperCase()}</div>
          <span className="user-email">{user.email}</span>
          <button onClick={logout} className="btn-logout">Logout</button>
        </div>
      </header>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<CreditCheckPage />} />
          <Route path="/mappings" element={<FieldMappingsPage />} />
          <Route path="/clients" element={<ProcessedClientsPage />} />
          <Route path="/logs" element={<AuditLogsPage />} />
          <Route path="/settings" element={<AdminSettingsPage />} />
          {/* Keep old /admin route for backwards compat */}
          <Route path="/admin" element={<AdminSettingsPage />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/credit-check" element={<PublicCreditCheckPage />} />
      <Route path="*" element={
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      } />
    </Routes>
  );
}

export default App;
